if (!customElements.get("fabrics-form")) {
  class FabricsForm extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.appInitialized = false;
      this.optionsLoaded = false;
      this.optionsLoading = false;
      this.appObserver = null;
      this.appFormObserver = null;
      this.submitObserver = null;
      this.submitInFlight = false;
      this.submissionCompleted = false;
      this.selected_options = [];
      this.initialStep = this.classList.contains("fabrics--2") ? 2 : 1;
      this.appReadyPromise = new Promise((resolve) => {
        this.resolveAppReady = resolve;
      });

      this.refreshSelectors();
      this.observeAppEmbed();
      this.listenForDrawerOpen();
      this.bind();
    }

    refreshSelectors() {
      this.selectors = {
        drawer: this.closest("drawer-section"),
        form: this.querySelector(".form--validate"),
        options: this.querySelectorAll('input[type="checkbox"][name="fabrics"]'),
        next: this.querySelector("[data-next]"),
        submit: this.querySelector('button[type="submit"]'),
        limit_msg: this.querySelector("[data-limit-message]"),
        app: this.querySelector(".fabrics__app"),
        app_form: this.selectors ? this.selectors.app_form : void 0,
        lazy_options: this.querySelector("[data-fabrics-options]"),
        selected: this.querySelector("[data-selected-fabrics]"),
        selected_slots: this.querySelector("[data-selected-fabrics-slots]"),
        back_to_fabrics: this.querySelectorAll("[data-back-to-fabrics]"),
        submit_error: this.querySelector("[data-submit-error]"),
      };
    }

    observeAppEmbed() {
      if (!this.selectors.app || this.selectors.app_form || this.appObserver) return;

      this.resolveAppForm();
      if (this.selectors.app_form) return;

      this.appObserver = new MutationObserver(() => {
        this.resolveAppForm();
      });

      this.appObserver.observe(this.selectors.app, {
        childList: true,
        subtree: true,
      });
    }

    resolveAppForm() {
      const appEmbed = this.selectors.app.querySelector("#app-embed");
      if (!appEmbed || !appEmbed.shadowRoot) return;

      const appForm = appEmbed.shadowRoot;
      const submit = appForm.querySelector('button[type="submit"]');

      if (!submit) {
        if (!this.appFormObserver) {
          this.appFormObserver = new MutationObserver(() => {
            this.resolveAppForm();
          });
          this.appFormObserver.observe(appForm, {
            childList: true,
            subtree: true,
          });
        }
        return;
      }

      this.appObserver?.disconnect();
      this.appObserver = null;
      this.appFormObserver?.disconnect();
      this.appFormObserver = null;
      this.selectors.app_form = appForm;
      this.bind();
      this.resolveAppReady(this.selectors.app_form);
    }

    listenForDrawerOpen() {
      if (!this.selectors.drawer) return;

      window.addEventListener("drawerOpen", (event) => {
        if (event.detail.id === this.selectors.drawer.id) {
          if (this.hasCompletedSubmission()) {
            this.completeSubmission();
          } else {
            this.showStep(this.initialStep);
          }
          this.loadOptions();
        }
      });
    }

    waitForAppForm() {
      if (this.selectors.app_form) {
        return Promise.resolve(this.selectors.app_form);
      }

      return Promise.race([
        this.appReadyPromise,
        new Promise((resolve, reject) => {
          window.setTimeout(
            () => reject(new Error("Shopify Forms did not initialize in time")),
            15000,
          );
        }),
      ]);
    }

    setSubmitInFlight(inFlight) {
      this.submitInFlight = inFlight;
      this.classList.toggle("loading", inFlight);

      if (!this.selectors.submit) return;

      this.selectors.submit.disabled =
        inFlight || this.selected_options.length === 0;

      if (inFlight) {
        this.selectors.submit.setAttribute("aria-busy", "true");
      } else {
        this.selectors.submit.removeAttribute("aria-busy");
      }
    }

    setSubmitError(visible) {
      if (this.selectors.submit_error) {
        this.selectors.submit_error.hidden = !visible;
      }
    }

    hasCompletedSubmission(appForm = this.selectors.app_form) {
      return (
        this.submissionCompleted ||
        Boolean(appForm?.querySelector('[data-current-step="success"]'))
      );
    }

    completeSubmission() {
      this.submissionCompleted = true;
      this.showStep(3);
      this.setSubmitInFlight(false);
      this.setSubmitError(false);
    }

    showStep(step) {
      this.classList.remove("fabrics--1", "fabrics--2", "fabrics--3");
      this.classList.add(`fabrics--${step}`);

      if (this.selectors.drawer) {
        this.selectors.drawer.dispatchEvent(new CustomEvent("drawerResize"));
      }
    }

    loadOptions() {
      const target = this.selectors.lazy_options;
      const url = target && target.dataset.lazyUrl;

      if (!target || !url || this.optionsLoaded || this.optionsLoading) {
        this.bind();
        return;
      }

      this.optionsLoading = true;
      this.classList.add("loading");

      fetch(url, { method: "GET" })
        .then((response) => {
          if (!response.ok) throw new Error("HTTP " + response.status);
          return response.text();
        })
        .then((html) => {
          target.innerHTML = html;
          this.optionsLoaded = true;
          this.optionsLoading = false;
          this.classList.remove("loading");
          this.refreshSelectors();
          this.observeAppEmbed();
          this.bind();

          if (this.selectors.drawer) {
            this.selectors.drawer.dispatchEvent(new CustomEvent("drawerResize"));
          }
        })
        .catch((error) => {
          this.optionsLoading = false;
          this.classList.remove("loading");
          console.error("Error loading fabric options:", error);
        });
    }

    getCheckedOptions() {
      return [...this.selectors.options].filter((item) => item.checked);
    }

    updateSelection(changedOption) {
      let checkedOptions = this.getCheckedOptions();

      if (checkedOptions.length > 5 && changedOption) {
        changedOption.checked = false;
        checkedOptions = this.getCheckedOptions();
      }

      this.selected_options = checkedOptions.map((item) => item.value);

      const hasSelection = this.selected_options.length > 0;
      const isFull = this.selected_options.length >= 5;

      this.selectors.next.disabled = !hasSelection;
      this.selectors.submit.disabled = !hasSelection || this.submitInFlight;
      this.selectors.limit_msg.style.display = isFull ? "flex" : "none";

      if (this.selectors.selected) {
        this.selectors.selected.classList.toggle("fabrics-selected--full", isFull);
      }

      this.syncSelectedFabricsInput();
      this.renderSelectedFabrics();
    }

    syncSelectedFabricsInput() {
      if (!this.selectors.app_form) return;

      const fabricsInput = this.selectors.app_form.querySelector(
        '[id="custom#wybrane_tkaniny"]',
      );

      if (fabricsInput) {
        fabricsInput.value = this.selected_options.join(", ");
        fabricsInput.dispatchEvent(new Event("input"));
      }
    }

    renderSelectedFabrics() {
      const slots = this.selectors.selected_slots;
      if (!slots) return;

      const selected = this.getCheckedOptions();
      const emptyLabel = this.selectors.selected.dataset.emptyLabel;
      const removeLabel = this.selectors.selected.dataset.removeLabel;

      slots.innerHTML = "";

      for (let index = 0; index < 5; index += 1) {
        const option = selected[index];
        const slot = document.createElement("button");
        slot.type = "button";
        slot.classList.add("fabrics-selected__slot");

        if (option) {
          const label = removeLabel.includes("{{ fabric }}")
            ? removeLabel.replace("{{ fabric }}", option.value)
            : `${removeLabel} ${option.value}`.trim();
          const preview = option
            .closest(".fabrics__item")
            ?.querySelector(".fabrics__item-preview")
            ?.cloneNode(true);
          const remove = document.createElement("span");

          slot.classList.add("fabrics-selected__slot--filled");
          slot.dataset.selectedFabricsRemove = "true";
          slot.dataset.fabricHandle = option.dataset.fabricHandle;
          slot.setAttribute("aria-label", label);

          if (preview) {
            preview.removeAttribute("for");
            preview.removeAttribute("id");
            slot.appendChild(preview);
          } else {
            const fallback = document.createElement("span");

            fallback.classList.add("fabrics-selected__fallback");
            fallback.textContent = option.value;
            slot.appendChild(fallback);
          }

          remove.classList.add("fabrics-selected__remove");
          remove.setAttribute("aria-hidden", "true");
          slot.appendChild(remove);
        } else {
          const icon = document.createElement("span");

          slot.classList.add("fabrics-selected__slot--empty");
          slot.dataset.selectedFabricsEmpty = "true";
          slot.setAttribute("aria-label", emptyLabel);

          icon.classList.add("fabrics-selected__plus");
          icon.setAttribute("aria-hidden", "true");
          slot.appendChild(icon);
        }

        slots.appendChild(slot);
      }
    }

    handleSelectedSlotClick(event) {
      const remove = event.target.closest("[data-selected-fabrics-remove]");
      const empty = event.target.closest("[data-selected-fabrics-empty]");

      if (remove && this.selectors.selected_slots.contains(remove)) {
        event.preventDefault();
        this.removeSelectedFabric(remove.dataset.fabricHandle);
        return;
      }

      if (empty && this.selectors.selected_slots.contains(empty)) {
        event.preventDefault();
        this.showStep(1);
        this.scrollToOptions();
      }
    }

    removeSelectedFabric(handle) {
      if (!handle) return;

      const option = [...this.selectors.options].find(
        (item) => item.dataset.fabricHandle === handle,
      );
      if (!option) return;

      option.checked = false;
      option.dispatchEvent(new Event("change", { bubbles: true }));
      this.syncSourceOption(handle, false);

      if (this.getCheckedOptions().length === 0) {
        this.showStep(1);
      }
    }

    syncSourceOption(handle, checked) {
      if (!handle) return;

      document
        .querySelectorAll('.fabrics--full input[type="checkbox"][name="fabrics"]')
        .forEach((input) => {
          if (input.dataset.fabricHandle !== handle || input.checked === checked) return;

          input.checked = checked;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
    }

    scrollToOptions() {
      const target =
        this.querySelector('[data-form-step="1"] .fabrics__group') ||
        this.selectors.lazy_options;

      if (target) {
        target.scrollIntoView({ block: "start" });
      }
    }

    bind() {
      this.refreshSelectors();

      if (
        !this.initialized &&
        this.selectors.form &&
        this.selectors.next &&
        this.selectors.submit &&
        this.selectors.limit_msg &&
        this.selectors.options.length > 0
      ) {
        this.selectors.options.forEach((option) => {
          option.addEventListener("change", () => {
            this.updateSelection(option);
            this.syncSourceOption(option.dataset.fabricHandle, option.checked);
          });
        });

        if (this.selectors.selected_slots) {
          this.selectors.selected_slots.addEventListener("click", (event) => {
            this.handleSelectedSlotClick(event);
          });
        }

        this.selectors.form.querySelectorAll("[data-sync]").forEach((input) => {
          input.addEventListener("change", () => {
            input.classList.add("touched");
            this.syncField(input);
          });
        });

        this.validate();
        this.updateSelection();

        this.selectors.next.addEventListener("click", () => {
          this.showStep(2);
        });

        this.selectors.back_to_fabrics.forEach((button) => {
          button.addEventListener("click", () => {
            this.showStep(1);
            this.scrollToOptions();
          });
        });

        this.selectors.submit.addEventListener("click", (event) => {
          event.preventDefault();

          if (this.submitInFlight) return;
          if (!this.validator.validate()) return;

          this.setSubmitError(false);
          this.setSubmitInFlight(true);
          this.waitForAppForm()
            .then((appForm) => {
              if (this.hasCompletedSubmission(appForm)) {
                this.completeSubmission();
                return;
              }

              this.syncAllFields();

              const submit = appForm.querySelector('button[type="submit"]');
              if (!submit) {
                throw new Error("Shopify Forms submit button is unavailable");
              }

              submit.click();
            })
            .catch((error) => {
              this.setSubmitInFlight(false);
              this.setSubmitError(true);
              console.error("Error submitting fabrics form:", error);
            });
        });

        this.initialized = true;
      }

      if (this.selectors.app_form && !this.appInitialized) {
        this.syncAllFields();

        this.submitObserver = new MutationObserver((entries) => {
          for (const entry of entries) {
            if (
              entry.type === "attributes" &&
              entry.attributeName === "data-current-step"
            ) {
              const currentStep = entry.target.getAttribute("data-current-step");

              if (currentStep === "success") {
                this.completeSubmission();
              } else if (currentStep === "error") {
                this.setSubmitInFlight(false);
                this.setSubmitError(true);
              }
            }
          }
        });

        this.submitObserver.observe(this.selectors.app_form, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        this.appInitialized = true;
      }
    }

    syncField(input) {
      if (!this.selectors.app_form) return;

      const target = this.selectors.app_form.querySelector(
        `[id="${input.getAttribute("data-sync")}"]`,
      );

      if (!target) return;

      if (input.type === "checkbox") {
        if (target.checked !== input.checked) {
          target.click();
        }
      } else {
        target.value = input.value;
        target.dispatchEvent(new Event("input"));
      }
    }

    syncAllFields() {
      if (!this.selectors.app_form || !this.selectors.form) return;

      this.selectors.form.querySelectorAll("[data-sync]").forEach((input) => {
        this.syncField(input);
      });
      this.syncSelectedFabricsInput();
    }

    validate() {
      this.validator = new Pristine(this.selectors.form, {
        classTo: "form__field",
        errorClass: "form__row--error",
        errorTextParent: "form__field",
        errorTextClass: "form__error",
      });

      const phone = this.selectors.form.querySelector('input[type="tel"]');

      if (phone) {
        this.validator.addValidator(
          phone,
          function (value) {
            return (
              !(value.length > 0) ||
              /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/.test(value)
            );
          },
          phone.getAttribute("data-pristine-phone-message"),
          5,
          false,
        );
      }

      this.querySelectorAll("select").forEach((select) => {
        const placeholder = select.querySelector("option[disabled]");
        if (!placeholder) return;

        this.validator.addValidator(
          select,
          function (value) {
            return value !== placeholder.innerText;
          },
          select.getAttribute("data-pristine-required-message"),
          5,
          false,
        );
      });
    }
  }

  customElements.define("fabrics-form", FabricsForm);
}
